import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = (process.env.REACT_APP_API_BASE || '').replace(/\/api$/, '') || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);

const PRESET_FORM = {
  name: 'Book Appointment',
  slug: 'book-appointment',
  title: 'Book Your Dental Appointment',
  description: 'Fill this form and we will contact you within 30 minutes',
  fields: [
    { name: 'name', label: 'Full Name', type: 'text', required: true },
    { name: 'phone', label: 'Phone Number', type: 'phone', required: true },
    { name: 'email', label: 'Email', type: 'email', required: false },
    { name: 'service', label: 'Service Needed', type: 'select', required: true,
      options: ['Cleaning', 'Filling', 'Root Canal', 'Extraction', 'Consultation', 'Other'] },
    { name: 'preferredDate', label: 'Preferred Date', type: 'date', required: false },
    { name: 'message', label: 'Additional Notes', type: 'textarea', required: false }
  ],
  submitButtonText: 'Book Now',
  successMessage: 'Thanks! Our team will call you shortly.'
};

export default function WhatsAppLeadFormsPage() {
  const [forms, setForms] = useState([]);
  const [editing, setEditing] = useState(null);
  const [viewingSubs, setViewingSubs] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const headers = { Authorization: `Bearer ${localStorage.getItem('dentalToken')}` };

  const load = async () => {
    try { const { data } = await axios.get(`${API}/api/saas/wa-forms`, { headers }); setForms(data); }
    catch (err) { console.error(err); }
  };
  useEffect(() => { load(); }, []);

  const createPreset = async () => {
    try { await axios.post(`${API}/api/saas/wa-forms`, PRESET_FORM, { headers }); load(); }
    catch (err) { alert(err.response?.data?.error || 'Failed'); }
  };

  const save = async () => {
    try {
      if (editing._id) await axios.patch(`${API}/api/saas/wa-forms/${editing._id}`, editing, { headers });
      else await axios.post(`${API}/api/saas/wa-forms`, editing, { headers });
      setEditing(null); load();
    } catch (err) { alert(err.response?.data?.error || 'Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete form?')) return;
    await axios.delete(`${API}/api/saas/wa-forms/${id}`, { headers });
    load();
  };

  const viewSubs = async (form) => {
    setViewingSubs(form);
    const { data } = await axios.get(`${API}/api/saas/wa-forms/${form._id}/submissions`, { headers });
    setSubmissions(data);
  };

  const updateSubStatus = async (subId, status) => {
    await axios.patch(`${API}/api/saas/wa-forms/submissions/${subId}`, { status }, { headers });
    viewSubs(viewingSubs);
  };

  const publicUrl = (form) => `${API}/api/public/forms/YOUR_TENANT_SLUG/${form.slug}`;

  const addField = () => {
    setEditing({ ...editing, fields: [...editing.fields, { name: '', label: '', type: 'text', required: false }] });
  };

  const updateField = (i, patch) => {
    const newFields = [...editing.fields];
    newFields[i] = { ...newFields[i], ...patch };
    setEditing({ ...editing, fields: newFields });
  };

  const removeField = (i) => {
    setEditing({ ...editing, fields: editing.fields.filter((_, idx) => idx !== i) });
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>📝 Lead Capture Forms</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={createPreset} style={btnSecondary}>📋 Preset: Book Appointment</button>
          <button onClick={() => setEditing({ name: '', slug: '', title: '', description: '', fields: [{ name: 'name', label: 'Name', type: 'text', required: true }], submitButtonText: 'Submit', successMessage: 'Thank you!' })} style={btnPrimary}>+ New Form</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
        {forms.map(f => (
          <div key={f._id} style={card}>
            <strong>{f.name}</strong>
            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>/{f.slug}</div>
            <div style={{ fontSize: 13, color: '#666', marginTop: 8 }}>{f.description || '-'}</div>
            <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
              {f.fields.length} fields · {f.submissionCount} submissions
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
              <button onClick={() => viewSubs(f)} style={miniBtn}>📊 Submissions</button>
              <button onClick={() => setEditing(f)} style={miniBtn}>Edit</button>
              <button onClick={() => remove(f._id)} style={{ ...miniBtn, color: '#ef4444' }}>×</button>
            </div>
            <div style={{ marginTop: 8, fontSize: 11, color: '#888', wordBreak: 'break-all' }}>
              Public URL: {publicUrl(f)}
            </div>
          </div>
        ))}
        {forms.length === 0 && <div style={{ gridColumn: '1/-1', textAlign: 'center', color: '#888', padding: 40 }}>No forms yet</div>}
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing._id ? 'Edit Form' : 'New Form'}>
          <input placeholder="Form name" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} style={input} />
          <input placeholder="URL slug (e.g. book-appt)" value={editing.slug} onChange={e => setEditing({ ...editing, slug: e.target.value })} style={input} />
          <input placeholder="Title (shown to user)" value={editing.title || ''} onChange={e => setEditing({ ...editing, title: e.target.value })} style={input} />
          <textarea placeholder="Description" value={editing.description || ''} onChange={e => setEditing({ ...editing, description: e.target.value })} style={{ ...input, minHeight: 60 }} />
          <input placeholder="Submit button text" value={editing.submitButtonText || ''} onChange={e => setEditing({ ...editing, submitButtonText: e.target.value })} style={input} />
          <input placeholder="Success message" value={editing.successMessage || ''} onChange={e => setEditing({ ...editing, successMessage: e.target.value })} style={input} />

          <div style={{ marginTop: 12, marginBottom: 8 }}>
            <strong>Fields</strong>
            <button onClick={addField} style={{ ...miniBtn, marginLeft: 8 }}>+ Add Field</button>
          </div>
          {editing.fields.map((f, i) => (
            <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 10, marginBottom: 8 }}>
              <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                <input placeholder="name (key)" value={f.name} onChange={e => updateField(i, { name: e.target.value })} style={{ ...input, flex: 1, marginBottom: 0 }} />
                <input placeholder="label" value={f.label} onChange={e => updateField(i, { label: e.target.value })} style={{ ...input, flex: 1, marginBottom: 0 }} />
                <select value={f.type} onChange={e => updateField(i, { type: e.target.value })} style={{ ...input, width: 110, marginBottom: 0 }}>
                  <option>text</option><option>email</option><option>phone</option><option>number</option>
                  <option>textarea</option><option>select</option><option>date</option>
                </select>
                <button onClick={() => removeField(i)} style={{ ...miniBtn, color: '#ef4444' }}>×</button>
              </div>
              <label style={{ fontSize: 12 }}>
                <input type="checkbox" checked={f.required} onChange={e => updateField(i, { required: e.target.checked })} /> Required
              </label>
              {f.type === 'select' && (
                <input placeholder="options (comma separated)" value={(f.options || []).join(', ')} onChange={e => updateField(i, { options: e.target.value.split(',').map(o => o.trim()).filter(Boolean) })} style={{ ...input, marginTop: 6 }} />
              )}
            </div>
          ))}
          <button onClick={save} style={btnPrimary}>Save Form</button>
        </Modal>
      )}

      {viewingSubs && (
        <Modal onClose={() => setViewingSubs(null)} title={`Submissions: ${viewingSubs.name}`}>
          <div style={{ maxHeight: 500, overflowY: 'auto' }}>
            {submissions.map(s => (
              <div key={s._id} style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 12, marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 12, color: '#666' }}>{new Date(s.createdAt).toLocaleString()}</span>
                  <select value={s.status} onChange={e => updateSubStatus(s._id, e.target.value)} style={{ fontSize: 12 }}>
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="converted">Converted</option>
                    <option value="lost">Lost</option>
                  </select>
                </div>
                {Object.entries(s.data).map(([k, v]) => (
                  <div key={k} style={{ fontSize: 13 }}><strong>{k}:</strong> {String(v)}</div>
                ))}
              </div>
            ))}
            {submissions.length === 0 && <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>No submissions yet</div>}
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ children, onClose, title }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: 8, padding: 24, width: '90%', maxWidth: 640, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

const input = { padding: 8, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, marginBottom: 8, width: '100%', boxSizing: 'border-box' };
const btnPrimary = { padding: '8px 16px', background: '#25D366', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 };
const btnSecondary = { padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer' };
const miniBtn = { padding: '4px 10px', fontSize: 12, background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer' };
const card = { background: 'white', padding: 16, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
